import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, FileText, Phone, Mail } from "lucide-react";

export function SolicitationsList({
  solicitations,
  onCreateQuotation,
  onApprove,
  onReject,
}) {
  const [selectedSolicitation, setSelectedSolicitation] = useState(null);
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [quotationData, setQuotationData] = useState({
    amount: "",
    description: "",
  });

  const getTypeLabel = (type) => {
    const labels = {
      system_development: "Desenvolvimento de Sistema",
      crm_escolar: "CRM Escolar",
      consulting: "Consultoria",
      other: "Outro",
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { color: "yellow", label: "Pendente" },
      approved: { color: "green", label: "Aprovado" },
      quotation_sent: { color: "blue", label: "Cotação Enviada" },
      rejected: { color: "red", label: "Rejeitado" },
    };
    const config = variants[status] || variants.pending;
    return (
      <Badge variant={config.color === "green" ? "default" : "secondary"}>
        {config.label}
      </Badge>
    );
  };

  const handleQuotationSubmit = async (e) => {
    e.preventDefault();
    if (
      selectedSolicitation &&
      quotationData.amount &&
      quotationData.description
    ) {
      await onCreateQuotation({
        requestId: selectedSolicitation.id,
        amount: parseFloat(quotationData.amount),
        description: quotationData.description,
      });
      setShowQuotationForm(false);
      setQuotationData({ amount: "", description: "" });
      setSelectedSolicitation(null);
    }
  };

  if (solicitations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Solicitações</CardTitle>
          <CardDescription>Nenhuma solicitação no momento</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        {solicitations.map((solicitation) => (
          <Card key={solicitation.id} className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-lg">
                      {getTypeLabel(solicitation.type)}
                    </CardTitle>
                    {getStatusBadge(solicitation.status)}
                  </div>
                  <CardDescription className="mt-2">
                    {solicitation.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">
                    {solicitation.userEmail}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">
                    {solicitation.userPhone}
                  </span>
                </div>
                <div className="col-span-2 text-xs text-gray-500">
                  Criada em:{" "}
                  {new Date(solicitation.createdAt).toLocaleDateString("pt-BR")}
                </div>
              </div>

              {solicitation.status === "pending" && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedSolicitation(solicitation);
                      setShowQuotationForm(true);
                    }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Enviar Cotação
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onApprove(solicitation.id)}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aprovar
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => onReject(solicitation.id)}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rejeitar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {showQuotationForm && selectedSolicitation && (
        <Card className="border-vitapink-200 bg-vitapink-50">
          <CardHeader>
            <CardTitle>Criar Cotação</CardTitle>
            <CardDescription>
              Para: {selectedSolicitation.userEmail}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleQuotationSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor da Cotação (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={quotationData.amount}
                  onChange={(e) =>
                    setQuotationData({
                      ...quotationData,
                      amount: e.target.value,
                    })
                  }
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vitapink-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição da Cotação
                </label>
                <textarea
                  value={quotationData.description}
                  onChange={(e) =>
                    setQuotationData({
                      ...quotationData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Detalhe da cotação..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vitapink-500"
                  required
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  className="bg-vitapink-500 hover:bg-vitapink-600"
                >
                  Enviar Cotação
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowQuotationForm(false);
                    setSelectedSolicitation(null);
                    setQuotationData({ amount: "", description: "" });
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
